import React from 'react';
import { InputCaptureKeyboardShortcut } from '../InputCaptureKeyboardShortcut';
import { Toggle } from '../../../../Components/Toggle';
import { useKeyboardManagerState } from '../../hooks/useKeyboardManagerState';
import { TItem, TResetShortcut, TUpdateShortcutView } from '../types';
import { getPotencialConflict } from '../../redux/reducer';
import { Conflict, KeyboardShortcutState } from '../../redux/types';

const commonEventProperties = {
    bubbles: true, // bubble event to containing elements
    composed: true, // let the event pass through the shadowDOM boundary
};

function UpdateShortcut({ parentId, item, setSavedNotification, setResetShortcut, setFeedbackType }: TUpdateShortcutView) {
    const { state, dispatch } = useKeyboardManagerState();
    const { id, name, isDesactivatable, isOverridable } = item;
    const itemState = state.shortcuts[item.id];

    const [conflict, setConflict] = React.useState<Conflict | null>(null);

    function focusOnShortcutResetButton(id: string) {
        setTimeout(() => {
            const resetButton = document.getElementById(`input-capture-reset-${id}`);
            resetButton?.focus();
        }, 100);
    }

    function conflictResolve() {
        if (!conflict) {
            return;
        }
        focusOnShortcutResetButton(conflict.id);

        dispatch({ type: 'OVERRIDE_CONNFLICT', payload: conflict });
        // @ts-ignore
        document.dispatchEvent(
            new CustomEvent('ds-keyboard-shortcuts-override-conflict', {
                bubbles: true, // bubble event to containing elements
                composed: true, // let the event pass through the shadowDOM boundary
                detail: {
                    id: parentId,
                    payload: { conflict },
                },
            }),
        );
        setConflict(null);
    }

    return <>
        <InputCaptureKeyboardShortcut
            state={state.shortcuts[item.id]}
            aria-label="Record shortcut now"
            aria-live="assertive"
            className="mr-m"
            isOverridable={isOverridable}
            hasConflict={
                conflict?.id === id ||
                conflict?.conflictingKey === id
            }
            resetButtonProps={{
                id: `input-capture-reset-${id}`,
                onClick: () => {
                    dispatch({
                        type: 'RESET_SHORTCUT_KEYS',
                        payload: { id },
                    });
                    // @ts-ignore
                    document.dispatchEvent(
                        new CustomEvent(
                            'ds-keyboard-shortcuts-reset-keys',
                            {
                                ...commonEventProperties,
                                detail: {
                                    id: parentId,
                                    payload: { id },
                                },
                            },
                        ),
                    );
                    const resetKey = { isReset: true, shortcutName: name }
                    //@ts-ignore
                    setResetShortcut(prevState => ({
                        ...prevState,
                        ...resetKey
                    }));
                    setFeedbackType('resetShortcut');
                },
            }}
            onComplete={value => {
                const potencialConflict = getPotencialConflict(
                    state,
                    id,
                    value,
                );
                if (potencialConflict) {
                    setConflict({
                        type: 'KEYS',
                        id,
                        keys: value,
                        conflictingKey: potencialConflict,
                    });
                } else {
                    dispatch({
                        type: 'SET_SHORTCUT_KEYS',
                        payload: { id, keys: value },
                    });

                    // @ts-ignore
                    document.dispatchEvent(
                        new CustomEvent(
                            'ds-keyboard-shortcuts-set-keys',
                            {
                                ...commonEventProperties,
                                detail: {
                                    id: parentId,
                                    payload: {
                                        id,
                                        keys: value,
                                    },
                                },
                            },
                        ),
                    );
                    setSavedNotification(name);
                    setFeedbackType('changeShortcut');
                }
            }}
        />
        <Toggle
            id={`shortcut-is-in-page-${id}`}
            name={`${id}-isInPage`}
            // @ts-ignore
            aria-label={`Disable/Enable shortcut: ${name}`} // a11y-TODO: disable/enable depending on state
            disabled={isDesactivatable === false}
            onChange={() => {
                const potencialConflict = getPotencialConflict(
                    state,
                    id,
                );
                if (potencialConflict) {
                    setConflict({
                        type: 'ACTIVE',
                        id,
                        conflictingKey: potencialConflict,
                    });
                } else {
                    dispatch({
                        type: 'TOGGLE_IS_ACTIVE',
                        payload: { id },
                    });

                    // @ts-ignore
                    document.dispatchEvent(
                        new CustomEvent(
                            'ds-keyboard-shortcuts-toggle-is-active',
                            {
                                ...commonEventProperties,
                                detail: {
                                    id: parentId,
                                    payload: { id },
                                },
                            },
                        ),
                    );
                }
            }}
            checked={itemState.isActive}
            innerLabels={['On', 'Off']}
        >
            <span className="sr-only">Disable shortcut</span>
        </Toggle>
    </>
}

export default UpdateShortcut;